// widget/src/hooks/useRecorder.ts
import { useCallback, useEffect, useRef, useState } from "react"

export function useRecorder() {
  const [isRecording, setIsRecording] = useState(false)
  const [permissionDenied, setPermissionDenied] = useState(false)

  const streamRef = useRef<MediaStream | null>(null)
  const recorderRef = useRef<MediaRecorder | null>(null)
  const audioCtxRef = useRef<AudioContext | null>(null)
  const analyserRef = useRef<AnalyserNode | null>(null)
  const chunksRef = useRef<Blob[]>([])
  const resolveBlobRef = useRef<((blob: Blob | null) => void) | null>(null)

  const teardown = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop())
      streamRef.current = null
    }
    if (audioCtxRef.current && audioCtxRef.current.state !== "closed") {
      audioCtxRef.current.close().catch(() => {})
      audioCtxRef.current = null
    }
    analyserRef.current = null
    recorderRef.current = null
  }, [])

  const start = useCallback(async () => {
    if (typeof window === "undefined" || !navigator.mediaDevices) return false
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      streamRef.current = stream

      const Ctor =
        window.AudioContext ||
        (window as unknown as { webkitAudioContext: typeof AudioContext })
          .webkitAudioContext
      const ctx = new Ctor()
      const source = ctx.createMediaStreamSource(stream)
      const analyser = ctx.createAnalyser()
      analyser.fftSize = 1024
      source.connect(analyser)
      audioCtxRef.current = ctx
      analyserRef.current = analyser

      const mime = MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
        ? "audio/webm;codecs=opus"
        : "audio/webm"
      const recorder = new MediaRecorder(stream, { mimeType: mime })
      chunksRef.current = []
      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data)
      }
      recorder.onstop = () => {
        const blob = chunksRef.current.length
          ? new Blob(chunksRef.current, { type: mime })
          : null
        resolveBlobRef.current?.(blob)
        resolveBlobRef.current = null
        teardown()
      }
      recorder.start()
      recorderRef.current = recorder
      setIsRecording(true)
      return true
    } catch {
      setPermissionDenied(true)
      teardown()
      return false
    }
  }, [teardown])

  const stop = useCallback((): Promise<Blob | null> => {
    return new Promise((resolve) => {
      if (!recorderRef.current || recorderRef.current.state !== "recording") {
        teardown()
        setIsRecording(false)
        resolve(null)
        return
      }
      resolveBlobRef.current = (blob) => {
        setIsRecording(false)
        resolve(blob)
      }
      recorderRef.current.stop()
    })
  }, [teardown])

  useEffect(() => {
    return () => {
      if (recorderRef.current && recorderRef.current.state === "recording") {
        recorderRef.current.stop()
      }
      teardown()
    }
  }, [teardown])

  return { isRecording, permissionDenied, analyser: analyserRef, start, stop }
}
